"use client";

import React, { useEffect, useState, useRef } from "react";
import { getRegionPrice } from "services/listingService";
import dynamic from "next/dynamic";
import classnames from "classnames";
import ModalTextContent from "components/modal-text-content";

import styles from "./styles.module.css";

const Modal = dynamic(() => import("components/common/modal"), { ssr: false });

const RegionPrice = () => {
  const [state, setState] = useState({
    didVisible: false,
    originUrl: "/",
    pageAllUrl: "/",
    pagePathName: "/",
    pageSearch: "",
    priceAdvantageValue: null,
    parentPriceValue: null,
    currentPriceValue: null,
    currentLocationText: "",
    parentLocationName: "",
    tradeType: "",
    visibleLocationReportSheet: false,
    dataAcquisitionDate: "",
    locationType: "",
    parentLocationType: "",
    currentLocationAmortization: "",
    parentLocationAmortization: "",
    amortizationAdvantage: "",
    expandedContent: false,
  });

  const containerRef = useRef();
  const expandedContentText = state.expandedContent ? "Daha Az Gör" : "Devamını Gör";
  const innerWrapperClasses = classnames({
    [styles.innerWrapper]: true,
    [styles.innerWrapperOpened]: state.expandedContent,
  });

  const moreButtonClasses = classnames({
    [styles.moreWrapper]: true,
    [styles.nonHighlighted]: state.expandedContent,
  });

  const handleMoreButton = () => {
    setState((prevState) => ({ ...prevState, expandedContent: !prevState.expandedContent }));
  };

  const formatNumber = (value) => {
    if (!value) return;

    if (value >= 1_000_000) {
      return `${Math.floor((value / 1_000_000) * 10) / 10} Milyon TL`;
    } else if (value >= 1_000) {
      return `${Math.floor(value / 1_000)} Bin TL`;
    } else {
      return `%${value} daha düşük`;
    }
  };

  const clickLocationReportDetail = () => {
    setState((prevState) => ({ ...prevState, visibleLocationReportSheet: true }));
  };

  const handleCloseActionSheetClick = () => {
    setState((prevState) => ({ ...prevState, visibleLocationReportSheet: false }));
  };

  useEffect(() => {
    const { origin, href, pathname, search } = window.location;
    setState((prevState) => ({
      ...prevState,
      originUrl: origin,
      pageAllUrl: href,
      pagePathName: pathname,
      pageSearch: search || "",
    }));
  }, []);

  useEffect(() => {
    const pathName = state?.pageAllUrl?.substring(state?.originUrl?.length);
    if (pathName) {
      getRegionPrice(pathName).then((res) => {
        const priceAdvantage = formatNumber(res?.priceInfo?.priceAdvantage);
        const parentPrice = formatNumber(res?.priceInfo?.parentLocationPrice);
        const currentPrice = formatNumber(res?.priceInfo?.currentLocationPrice);
        const tradeTypeMap = { RENT: "kiralık", SALE: "satılık" };
        const locationMap = {
          ilindeki: "İldeki",
          ilçesindeki: "İlçedeki",
          Mahallesindeki: "Mahalledeki",
          Köyündeki: "Köyündeki",
        };
        const parentLocationMap = {
          İlçedeki: "İl",
          Mahalledeki: "İlçe",
          Köyündeki: "İlçe",
        };

        const locationType =
          Object.entries(locationMap).find(([keyword]) => res?.currentLocationText?.includes(keyword))?.[1] || "";
        const parentLocationType =
          Object.entries(parentLocationMap).find(([keyword]) => locationType.includes(keyword))?.[1] || "";

        setState((prevState) => ({
          ...prevState,
          priceAdvantageValue: priceAdvantage,
          parentPriceValue: parentPrice,
          currentPriceValue: currentPrice,
          parentLocationName: res?.parentLocationName,
          currentLocationText: res?.currentLocationText,
          currentLocationAmortization: res?.amortizationInfo?.currentLocationAmortization,
          parentLocationAmortization: res?.amortizationInfo?.parentLocationAmortization,
          amortizationAdvantage: res?.amortizationInfo?.amortizationAdvantage
            ? `%${res?.amortizationInfo?.amortizationAdvantage} daha düşük`
            : null,
          dataAcquisitionDate: res?.dataAcquisitionDate,
          tradeType: tradeTypeMap[res?.tradeType] || "",
          locationType,
          parentLocationType,
        }));
      });
    }
  }, [state?.pagePathName]);

  return (
    <>
      {(state.currentPriceValue || state.priceAdvantageValue) && (
        <div className={styles.container} ref={containerRef}>
          <div className={styles.area}>
            <div className={styles.price}>
              <img src="/assets/icons/analytic.svg" alt="analytic-report" />
              {state.priceAdvantageValue ? (
                <span>
                  {state.currentLocationText} ortalama {state.tradeType} ev fiyatı, {state.parentLocationName}{" "}
                  genelinden{" "}
                  <span className={styles.bold}>
                    <label>{state.priceAdvantageValue}</label>
                  </span>
                </span>
              ) : (
                <span>
                  {state.currentLocationText} ortalama {state.tradeType} ev fiyatı:{" "}
                  <span className={styles.bold}>{state.currentPriceValue}</span>
                </span>
              )}
            </div>
            <div className={styles.button} onClick={clickLocationReportDetail}>
              Bölge Raporunu İncele
            </div>
          </div>
        </div>
      )}
      <Modal title="Bölge Raporu" isOpen={state.visibleLocationReportSheet} handleClose={handleCloseActionSheetClick}>
        <ModalTextContent>
          <div className={styles.actionsheet}>
            <div className={styles.border}>
              {state.priceAdvantageValue && (
                <div className={styles.priceAdvantage}>
                  {state.locationType} ortalama {state.tradeType} ev fiyatı, {state.parentLocationType} genelinden{" "}
                  <span>{state.priceAdvantageValue}</span>
                </div>
              )}
              <div className={styles.field}>
                <div className={styles.amortization}>
                  <div className={styles.amortizationText}>{state.locationType} Ortalama Fiyat</div>
                  <label>{state.currentPriceValue}</label>
                </div>
                {state.parentLocationType && (
                  <>
                    <div className={styles.line}></div>
                    <div className={styles.amortization}>
                      <div className={styles.amortizationText}>{state.parentLocationType}deki Ortalama Fiyat</div>
                      <label>{state.parentPriceValue}</label>
                    </div>
                  </>
                )}
              </div>
              {state.amortizationAdvantage && (
                <div className={styles.priceAdvantage}>
                  {state.locationType} tahmini geri dönüş süresi, {state.parentLocationType} genelinden{" "}
                  <span>{state.amortizationAdvantage}</span>
                </div>
              )}
              {state.tradeType === "satılık" && (
                <div className={styles.field}>
                  <div className={styles.amortization}>
                    <div className={styles.amortizationContanier}>
                      <div className={styles.amortizationText}>{state.locationType} Tahmini Geri Dönüş Süresi</div>
                      <span className={styles.iconWrap}>
                        <img
                          className={styles.iconImage}
                          src="/assets/icons/round-info-24-px-copy.svg"
                          alt="round-info-24-px-copy.svg"
                        />
                      </span>
                    </div>
                    {state.currentLocationAmortization && <label>{state.currentLocationAmortization} Yıl</label>}
                  </div>
                  {state.parentLocationAmortization && (
                    <>
                      <div className={styles.line}></div>
                      <div className={styles.amortization}>
                        <div className={styles.amortizationContanier}>
                          <div className={styles.amortizationText}>
                            {state.parentLocationType}deki Tahmini Geri Dönüş Süresi
                          </div>
                          <span className={styles.iconWrap}>
                            <img
                              className={styles.iconImage}
                              src="/assets/icons/round-info-24-px-copy.svg"
                              alt="round-info-24-px-copy.svg"
                            />
                          </span>
                        </div>
                        <label>{state.parentLocationAmortization} Yıl</label>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className={styles.date}>Veri Edinme Tarihi: {state.dataAcquisitionDate}</div>
            <div className={styles.line}></div>
            <div className={styles.wrapper}>
              <div className={innerWrapperClasses}>
                <span>
                  Bu ekrandaki tahminler, endeksa tarafından satış, saha çalışmaları ve internette yer alan verilere
                  dayalı istatistiksel modelleme yöntemleri ile üretilmiştir ve sapmalar içerebilir. Burada yer alan
                  bilgiler ve tahminler, varsayımsal olup herhangi bir taahhüt veya kesinlik içermez. Bu kapsamda
                  buradaki bilgiler ve tahminler, müşteri için sadece tavsiye niteliğinde olup öngörü amaçlıdır;
                  herhangi bir şekilde Emlakjet, endeksa veya müşteriler için hukuki bağlayıcılığı olamaz. Bu bilgi ve
                  tahminlerin bir yatırıma veya ticarete konu edilmesi halinde Emlakjet ve endeksa herhangi bir
                  sorumluluk üstlenmez
                </span>
              </div>
              <div className={moreButtonClasses} onClick={handleMoreButton}>
                <span>{expandedContentText}</span>
              </div>
            </div>
          </div>
        </ModalTextContent>
      </Modal>
    </>
  );
};

export default RegionPrice;
